import { useMemo, useState } from 'react';

import {
    type FileUpload,
    type FileUploadStaged,
    initialiseFileUpload,
    initialiseRejectedFileUpload,
    prepareStagedFileUploads,
} from '../../model/fileUpload';
import type { RejectedFile } from '../../model/rejectedFile';
import {
    toRemoved,
    updateError,
    updateProgress,
    updateStaged,
    updateSuccess,
    updateTargetFileUpload,
    updateTargetFileUploadByFile,
} from './styleGuideUploadStatusUpdateUtils';

const useStyleGuideUploadStatus = () => {
    const [uploads, setUploads] = useState<FileUpload[] | null>(null);

    return [
        uploads,
        useMemo(
            () => ({
                addFiles: (files: File[], rejectedFiles?: RejectedFile[]) => {
                    setUploads((state) => [
                        ...(rejectedFiles ?? []).map(initialiseRejectedFileUpload),
                        ...files.map(initialiseFileUpload),
                        ...(state ?? []),
                    ]);
                },

                hasBeenAdded: (file: File) => uploads?.some((fileUpload) => fileUpload.file === file) === true,

                remove: (fileUpload: FileUpload) => {
                    setUploads((state) =>
                        updateTargetFileUploadByFile(
                            state ?? [],
                            fileUpload.file,
                            ({ file, uploadId }: FileUpload): FileUpload => ({ file, uploadId, state: 'removed' }),
                        ),
                    );
                },

                setRemoving: (fileUpload: FileUpload) => {
                    setUploads((state) => updateTargetFileUpload(state ?? [], fileUpload, toRemoved));
                },

                setLoadingProgress: (file: File, progressPercent: number) => {
                    setUploads((state) =>
                        updateTargetFileUploadByFile(state ?? [], file, updateProgress(progressPercent)),
                    );
                },

                setUploadSuccess: (file: File, styleGuideId: string) => {
                    setUploads((state) => updateTargetFileUploadByFile(state ?? [], file, updateSuccess(styleGuideId)));
                },

                setUploadError: (file: File, error: string, retry?: () => void) => {
                    setUploads((state) => updateTargetFileUploadByFile(state ?? [], file, updateError(error, retry)));
                },

                stageUpload: () => {
                    setUploads((state) => state?.map(prepareStagedFileUploads) ?? null);
                },

                updateStaged: (fileUpload: FileUpload, update: Partial<FileUploadStaged>) => {
                    setUploads((state) => updateTargetFileUpload(state ?? [], fileUpload, updateStaged(update)));
                },

                reset: () => {
                    setUploads(null);
                },
            }),
            [uploads],
        ),
    ] as const;
};

export default useStyleGuideUploadStatus;
